import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "t-table-header-sub" }

import { ref, Ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useStore } from '@/store'
import CScoreTable from '@/components/ranking/season/CScoreTable.vue'
import CSearchInput from '@/components/CSearchInput.vue'
import CSelectGroup from '@/components/CSelectGroup.vue'
import CSelect from '@/components/CSelect.vue'
import COverviewTable from '@/components/ranking/season/COverviewTable.vue'
import CTicketTable from '@/components/ranking/season/CTicketTable.vue'
import { SeasonRanking } from '@/model/ranking/Season'
import { State } from '@/store/state'
import { TableFilter as _TableFilter } from '@/model/ranking/TableFilter'
import { jslog } from '@gd/jslog'
import { track } from '@/lib/track'


export default /*@__PURE__*/_defineComponent({
  __name: 'MSeasonTable',
  props: {
    zoneId: {}
  },
  setup(__props: any) {

const props = __props
const route = useRoute()
const store = useStore()
const { t } = useI18n()

const rowsToRender = ref(0)
const currentPage = ref(0)
const TableFilter = _TableFilter
const ROWS_PER_BATCH = {
  [TableFilter.DEFAULT]: 50,
  [TableFilter.SCORES]: 50,
  [TableFilter.TICKETS]: 15,
}

const currentFilter: Ref<_TableFilter> = ref(TableFilter.DEFAULT)

const searchTerm = ref('')

const rankings = computed((): Array<SeasonRanking> => {
  return store.getters['ranking/season/rankings']()
})

const myRank = computed((): SeasonRanking => {
  return store.getters['ranking/season/myRankUi']
})

const lastQualifiedRank = computed((): number => {
  const rank = store.state.ranking.season.current.lastQualifiedRank
  return rank
})

const maxRows = computed((): number => {
  switch (currentFilter.value) {
    case TableFilter.SCORES:
      return isSearchActive.value
        ? scoreUsersRange.value.length
        : rankings.value.filter((item) => !item.ticketInfo).length
    case TableFilter.TICKETS:
      return isSearchActive.value
        ? ticketUsersRange.value.length
        : Math.floor(
            rankings.value.filter((item) => item.ticketInfo).length /
              (store.state as State).tournament.metaData.numberOfTickets
          )
    default:
      return isSearchActive.value ? seasonRankingsRange.value.length : rankings.value.length
  }
})

/**
 * Get amount of rows depending on selected filter
 */
const rowsPerLoadingBatch = computed((): number => {
  return ROWS_PER_BATCH[currentFilter.value]
})

/**
 * Consider search as active when minimum amount of characters is reached
 */
const isSearchActive = computed(() => {
  return searchTerm.value.length > 0
})

const filterOptions = computed(() => {
  return [
    { name: t('table.filters.masters_no_filter'), value: TableFilter.DEFAULT },
    { name: t('table.filters.masters_score_only'), value: TableFilter.SCORES },
    { name: t('table.filters.masters_tickets_only'), value: TableFilter.TICKETS },
  ]
})

function created() {
  store.dispatch('ranking/season/fetch', props.zoneId).catch((err) => {
    jslog.send({ name: 'fatal', data: { message: 'could not load season table' } })
    console.error(err)
  })
}

/**
 * Default table data
 */
const seasonRankingsRange = computed((): Array<SeasonRanking> => {
  return store.getters['ranking/season/rankings'](0, rowsToRender.value, searchTerm.value)
})

/**
 * Table data filtered for users who qualified by score
 */
const scoreUsersRange = computed((): Array<SeasonRanking> => {
  let rankings = store.getters['ranking/season/rankingsHighScoresOnly'](0, rowsToRender.value, searchTerm.value)
  if (rowsToRender.value <= lastQualifiedRank.value) {
    rankings = rankings.filter((ranking: SeasonRanking, index: number, arr: Array<SeasonRanking>) => {
      if (lastQualifiedRank.value > arr.length) {
        return ranking.seasonRank === '-' || (ranking.seasonRank as number) <= lastQualifiedRank.value
      } else {
        return (ranking.seasonRank as number) <= lastQualifiedRank.value
      }
    })
  }
  return rankings
})

/**
 * Table data filtered for users who qualified by ticket
 */
const ticketUsersRange = computed((): Array<SeasonRanking> => {
  return store.getters['ranking/season/rankingsTicketsOnly'](0, rowsToRender.value, searchTerm.value)
})

/**
 * Calculate page index and amount of entries to show initially and after switching table filters
 */
function setupTable(numRows: number) {
  rowsToRender.value = validateRowsAmount(numRows)
  const pageQuotient = rowsToRender.value / ROWS_PER_BATCH[currentFilter.value]
  currentPage.value = Math.ceil(pageQuotient)
  if (currentPage.value / pageQuotient < 1.25) {
    currentPage.value++
  }
}

/**
 * Returns amount of rows up to ranking-list end initially
 * but also returns memorized amount of rows when user switches filter-views back and forth
 */
function validateRowsAmount(rowAmount: number): number {
  if (currentFilter.value !== TableFilter.TICKETS && rowAmount <= lastQualifiedRank.value) {
    return lastQualifiedRank.value
  } else {
    return rowAmount
  }
}

/**
 * Event handling
 */
function onLoadMore() {
  if (rowsPerLoadingBatch.value * currentPage.value <= maxRows.value) {
    rowsToRender.value = rowsPerLoadingBatch.value * currentPage.value
    currentPage.value++

    track({
      id: 'seasonTable',
      elem: 'load-more',
      path: route.fullPath,
    })
  } else {
    rowsToRender.value = maxRows.value
  }
}

function onFilterChange(type: _TableFilter) {
  currentFilter.value = type
  setupTable(rowsToRender.value)
  track({
    id: 'seasonTable',
    elem: 'filter-changed',
    data: type,
    path: route.fullPath,
  })
}

function onSearchTerm(term: string) {
  searchTerm.value = term
}

/**
 * Calculate number of rows to display once lastQualifiedRank is set
 */
watch(
  lastQualifiedRank,
  (to: number) => {
    setupTable(to)
  },
  { immediate: true }
)

created()

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(CSelectGroup, { class: "filter-selection" }, {
        default: _withCtx(() => [
          _createVNode(CSelect, {
            value: filterOptions.value[0].value,
            options: filterOptions.value,
            onInput: onFilterChange,
            "qe-id": "table-filter"
          }, null, 8, ["value", "options"])
        ]),
        _: 1
      }),
      _createVNode(CSearchInput, {
        onOnChange: onSearchTerm,
        placeholder: _ctx.$t('table.search.input_placeholder'),
        "data-qe-id": "table-search-input"
      }, null, 8, ["placeholder"])
    ]),
    (currentFilter.value === _unref(TableFilter).DEFAULT)
      ? (_openBlock(), _createBlock(COverviewTable, {
          key: 0,
          data: seasonRankingsRange.value,
          isSearchActive: isSearchActive.value,
          lastQualifiedRank: lastQualifiedRank.value,
          maxRows: maxRows.value,
          myRank: myRank.value,
          onShowMore: onLoadMore,
          "data-qe-id": `table--${_unref(TableFilter).DEFAULT}`
        }, null, 8, ["data", "isSearchActive", "lastQualifiedRank", "maxRows", "myRank", "data-qe-id"]))
      : _createCommentVNode("", true),
    (currentFilter.value === _unref(TableFilter).SCORES)
      ? (_openBlock(), _createBlock(CScoreTable, {
          key: 1,
          data: scoreUsersRange.value,
          isSearchActive: isSearchActive.value,
          lastQualifiedRank: lastQualifiedRank.value,
          maxRows: maxRows.value,
          myRank: myRank.value,
          onShowMore: onLoadMore,
          "data-qe-id": `table--${_unref(TableFilter).SCORES}`
        }, null, 8, ["data", "isSearchActive", "lastQualifiedRank", "maxRows", "myRank", "data-qe-id"]))
      : _createCommentVNode("", true),
    (currentFilter.value === _unref(TableFilter).TICKETS)
      ? (_openBlock(), _createBlock(CTicketTable, {
          key: 2,
          data: ticketUsersRange.value,
          isSearchActive: isSearchActive.value,
          maxRows: maxRows.value,
          myRank: myRank.value,
          onShowMore: onLoadMore,
          "data-qe-id": `table--${_unref(TableFilter).TICKETS}`
        }, null, 8, ["data", "isSearchActive", "maxRows", "myRank", "data-qe-id"]))
      : _createCommentVNode("", true)
  ]))
}
}

})